import { useSnackbar } from '~/lib/hooks/useSnackbar';

import { ProblemFromSWR } from '../problemPreview';
import { useExamTemplateStore } from '~/work/teacherWork/[exerciseId]/stores/examTemplateStore';
import * as voodooSdk from '@gmm/sdk-voodoo';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { ProblemAndAnswer } from '@gmm/sdk-voodoo/shared';
import { LockedProblemResponse } from '@gmm/sdk-voodoo/examProblems';
import { useDuplicationStore } from '~/work/skillDiscovery/stores';

export interface LockedExamProblemParams {
  lockedExamIdentifier: string;
  skillId: string;
  positionWithinSkill: number;
}

export const useLockedExamTemplateProblem = (
  params?: LockedExamProblemParams,
  enabled?: boolean,
): ProblemFromSWR => {
  const { enqueueGenericError } = useSnackbar();
  const [getProblem, addProblem] = useExamTemplateStore(state => [
    state.getExamTemplateProblem,
    state.addExamTemplateProblem,
  ]);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<any>(undefined);
  const [prob, setProb] = useState<ProblemAndAnswer | undefined>(undefined);
  const removeDuplicatedFromSkillDiscovery = useDuplicationStore(
    state => state.removeDuplicatedFromSkillDiscoveryByIds,
  );

  const fetchProblem = async (): Promise<void> => {
    if (!params || !enabled) return;
    try {
      setError(undefined);
      const problem = getProblem(params);
      if (!problem) {
        setIsValidating(true);
        const config = voodooSdk.examProblems.show(
          params.lockedExamIdentifier,
          params.skillId,
          params.positionWithinSkill,
          false,
        );
        const { data } = await axios.request<LockedProblemResponse>(config);
        addProblem(params.lockedExamIdentifier, {
          skillId: params.skillId,
          positionWithinSkill: params.positionWithinSkill,
          problemAndAnswer: data.clientProblem,
          dirty: false,
          canRevert: !!data.canRevert,
        });
        setProb(data.clientProblem);
      } else {
        setProb(problem.problemAndAnswer);
      }
    } catch (error) {
      setError(error);
      enqueueGenericError();
    } finally {
      setIsValidating(false);
    }
  };

  const refreshExamTemplateProblem = async (): Promise<void> => {
    if (!params) return;
    try {
      setError(undefined);
      setIsValidating(true);
      const config = voodooSdk.examProblems.show(
        params.lockedExamIdentifier,
        params.skillId,
        params.positionWithinSkill,
        true,
      );
      const response = await axios.request<LockedProblemResponse>(config);
      addProblem(params.lockedExamIdentifier, {
        skillId: params.skillId,
        positionWithinSkill: params.positionWithinSkill,
        problemAndAnswer: response.data.clientProblem,
        dirty: true,
        canRevert: !!response.data.canRevert,
      });
      setProb(response.data.clientProblem);
      removeDuplicatedFromSkillDiscovery(
        params.skillId,
        params.positionWithinSkill,
      );
    } catch (error) {
      setError(error);
      enqueueGenericError();
    } finally {
      setIsValidating(false);
    }
  };

  useEffect(() => {
    if (!params || !enabled || isValidating) return;

    const problem = getProblem(params);
    if (!problem) {
      fetchProblem();
    } else {
      setProb(problem.problemAndAnswer); // Sync with store if already present
    }
  }, [params?.skillId, params?.positionWithinSkill, enabled]);

  return {
    problemData: enabled ? prob : undefined,
    refreshExamTemplateProblem,
    isValidating,
    error,
  };
};
